import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faExclamationTriangle, 
  faSpinner, 
  faEnvelope,
  faCalendar,
  faClock,
  faExchangeAlt,
  faArrowRight,
  faTimes,
  faHome,
  faFlask,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import config from '../../../config/environment';
import BookingCard from './BookingCard';
import Footer from '../../Footer/Footer';

const PortalHeader = styled.header`
  background: linear-gradient(135deg, #192f6a 0%, #2c4a8a 100%);
  color: white;
  padding: 2rem 0;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  position: relative;
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const BetaBadge = styled.span`
  background: rgba(255, 193, 7, 0.9);
  color: #000;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
`;

const SupportNotice = styled.span`
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.85rem;
  font-weight: 400;
  
  a {
    color: rgba(255, 193, 7, 0.95);
    text-decoration: none;
    font-weight: 600;
    
    &:hover {
      text-decoration: underline;
      color: rgba(255, 193, 7, 1);
    }
  }
`;

const HomeLink = styled.a`
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-weight: 500;
  background: rgba(255,255,255,0.1);
  
  &:hover {
    background: rgba(255,255,255,0.2);
    transform: translateY(-2px);
  }
`;

const PortalTitle = styled.h1`
  margin: 0;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
`;

const PortalSubtitle = styled.p`
  margin: 0;
  font-size: 1rem;
  opacity: 0.95;
  font-weight: 400;
  margin-bottom: 0.75rem;
`;

const ComingSoon = styled.span`
  display: inline-block;
  margin-left: 0.5rem;
  font-size: 0.85rem;
  opacity: 0.8;
  font-style: normal;
`;

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding-bottom: 3rem;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const StepCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  margin-bottom: 2rem;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e9ecef;
`;

const StepNumber = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #192f6a 0%, #2c4a8a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const StepTitle = styled.h2`
  margin: 0;
  color: #192f6a;
  font-size: 1.75rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #333;
  font-size: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid #dee2e6;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #192f6a;
    box-shadow: 0 0 0 3px rgba(25, 47, 106, 0.1);
  }
`;

const Button = styled.button`
  background: ${props => {
    if (props.variant === 'secondary') return '#6c757d';
    if (props.variant === 'success') return 'linear-gradient(135deg, #28a745 0%, #218838 100%)';
    return 'linear-gradient(135deg, #192f6a 0%, #2c4a8a 100%)';
  }};
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  }
`;

const ErrorMessage = styled.div`
  background: #f8d7da;
  border: 2px solid #dc3545;
  color: #721c24;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SuccessMessage = styled.div`
  background: #d4edda;
  border: 2px solid #28a745;
  color: #155724;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoMessage = styled.div`
  background: #d1ecf1;
  border: 2px solid #17a2b8;
  color: #0c5460;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BookingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2rem;
`;

const ReminderBanner = styled.div`
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border: 2px solid #ffc107;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.2);
`;

const ReminderTitle = styled.div`
  font-weight: 700;
  font-size: 1.1rem;
  color: #856404;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ReminderText = styled.p`
  color: #856404;
  margin: 0.5rem 0;
  line-height: 1.6;
`;

const ManageBooking = () => {
  const [step, setStep] = useState(1);
  const [transactionRef, setTransactionRef] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [transferredBookings, setTransferredBookings] = useState([]);
  const [suggestedTransferDate, setSuggestedTransferDate] = useState('');

  const handleSendCode = async () => {
    if (!transactionRef.trim() || !email.trim()) {
      setError('Please enter both transaction reference and email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${config.apiEndpoint}/custom/v1/send-booking-verification-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transaction_reference: transactionRef.trim(),
          email: email.trim()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess('Verification code sent to your email. Please check your inbox.');
        setCodeSent(true);
        setStep(2);
      } else {
        setError(data.message || data.error?.message || 'Failed to send verification code. Please check your transaction reference and email address.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!code.trim() || code.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${config.apiEndpoint}/custom/v1/verify-booking-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transaction_reference: transactionRef.trim(),
          email: email.trim(),
          code: code.trim()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSessionToken(data.token);
        setSuccess('Verification successful! Loading your bookings...');
        setStep(3);
        
        // Fetch full booking details with VenPos information
        await fetchBookings(data.token);
      } else {
        setError(data.message || data.error?.message || 'Invalid verification code. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async (token) => {
    try {
      const response = await fetch(`${config.apiEndpoint}/custom/v1/get-customer-bookings?token=${token}`);

      const data = await response.json();

      if (response.ok && data.success) {
        setBookings(data.bookings || []);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
    }
  };

  const handleTransferSuccess = (bookingRef, transferDate) => {
    setSuccess(`Booking ${bookingRef} transferred successfully! An email confirmation has been sent.`);
    
    // Track this booking as transferred
    setTransferredBookings(prev => [...prev, bookingRef]);
    
    // Set suggested date for remaining bookings
    if (!suggestedTransferDate && transferDate) {
      setSuggestedTransferDate(transferDate);
    }
    
    // Refresh bookings
    if (sessionToken) {
      fetchBookings(sessionToken);
    }
  };

  return (
    <>
      <PortalHeader>
        <HeaderContent>
          <HeaderTop>
            <LeftSection>
              <BetaBadge>
                <FontAwesomeIcon icon={faFlask} />
                BETA VERSION
              </BetaBadge>
              <SupportNotice>
                If you have difficulties, please email <a href="mailto:techsupport@belvoircastle.com">techsupport@belvoircastle.com</a>
              </SupportNotice>
            </LeftSection>
            <HomeLink href="https://belvoircastle.com">
              <FontAwesomeIcon icon={faHome} />
              Back to Main Website
            </HomeLink>
          </HeaderTop>
          <PortalTitle>Manage Bookings Portal</PortalTitle>
          <PortalSubtitle>
            Your Portal for Managing Bookings
            <ComingSoon>• More features coming soon</ComingSoon>
          </PortalSubtitle>
        </HeaderContent>
      </PortalHeader>
      
      <Container>
        <ContentWrapper>
          <StepCard>
            <StepHeader>
              <StepNumber>{step}</StepNumber>
              <StepTitle>
                {step === 1 && 'Enter Your Booking Details'}
                {step === 2 && 'Verify Your Email'}
                {step === 3 && 'Manage Your Bookings'}
              </StepTitle>
            </StepHeader>

            {error && (
              <ErrorMessage>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {error}
              </ErrorMessage>
            )}

            {success && (
              <SuccessMessage>
                <FontAwesomeIcon icon={faCheckCircle} />
                {success}
              </SuccessMessage>
            )}

            {step === 1 && (
              <>
                <InfoMessage>
                  <FontAwesomeIcon icon={faEnvelope} />
                  Enter your transaction reference and email address to manage your booking. You'll receive a verification code via email.
                </InfoMessage>

                <FormGroup>
                  <Label>Transaction Reference *</Label>
                  <Input
                    type="text"
                    placeholder="e.g., BEL-COL-XXXXX-X"
                    value={transactionRef}
                    onChange={(e) => setTransactionRef(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendCode()}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendCode()}
                  />
                </FormGroup>

                <Button onClick={handleSendCode} disabled={loading || !transactionRef.trim() || !email.trim()}>
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Sending Code...
                    </>
                  ) : (
                    <>
                      Send Verification Code
                      <FontAwesomeIcon icon={faArrowRight} />
                    </>
                  )}
                </Button>
              </>
            )}

            {step === 2 && (
              <>
                <InfoMessage>
                  <FontAwesomeIcon icon={faEnvelope} />
                  We've sent a 6-digit verification code to <strong>{email}</strong>. Please check your inbox and enter the code below. The code expires in 15 minutes.
                </InfoMessage>

                <FormGroup>
                  <Label>Verification Code *</Label>
                  <Input
                    type="text"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    style={{ 
                      fontSize: '2rem', 
                      textAlign: 'center', 
                      letterSpacing: '0.5rem',
                      fontFamily: 'monospace'
                    }}
                    onKeyPress={(e) => e.key === 'Enter' && code.length === 6 && handleVerifyCode()}
                  />
                </FormGroup>

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <Button onClick={handleVerifyCode} disabled={loading || code.length !== 6} variant="success">
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />
                        Verifying...
                      </>
                    ) : (
                      <>
                        Verify Code
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="secondary" 
                    onClick={() => {
                      setCode('');
                      setCodeSent(false);
                      setStep(1);
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                    Back
                  </Button>
                </div>

                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={handleSendCode}
                    disabled={loading}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#192f6a',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    Resend Code
                  </button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <InfoMessage>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  You can now manage your bookings. Select a booking below to transfer it to a new date and time.
                </InfoMessage>

                {bookings.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                    <p>No bookings found for this transaction reference.</p>
                  </div>
                ) : (
                  <>
                    {transferredBookings.length > 0 && bookings.length > transferredBookings.length && (
                      <ReminderBanner>
                        <ReminderTitle>
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                          Multiple Bookings Reminder
                        </ReminderTitle>
                        <ReminderText>
                          <strong>You have {bookings.length} booking(s) in total.</strong>
                        </ReminderText>
                        <ReminderText>
                          You've transferred {transferredBookings.length} booking(s) so far.
                          {suggestedTransferDate && (
                            <> We've pre-selected the same date for your remaining booking(s) to keep them together.</>
                          )}
                        </ReminderText>
                        <ReminderText>
                          <strong>Reminder:</strong> If you want all your bookings on the same day, please transfer the remaining {bookings.length - transferredBookings.length} booking(s) to the same date.
                        </ReminderText>
                      </ReminderBanner>
                    )}
                    
                    <BookingsList>
                      {bookings.map((booking, index) => (
                        <BookingCard
                          key={booking.booking_reference || index}
                          booking={booking}
                          transactionRef={transactionRef}
                          sessionToken={sessionToken}
                          onTransferSuccess={handleTransferSuccess}
                          suggestedDate={suggestedTransferDate}
                          isTransferred={transferredBookings.includes(booking.booking_reference)}
                          totalBookings={bookings.length}
                        />
                      ))}
                    </BookingsList>
                  </>
                )}
              </>
            )}
          </StepCard>
        </ContentWrapper>
      </Container>
      <Footer />
    </>
  );
};

export default ManageBooking;
